'use client';
import { useState, useRef, useCallback, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import Icon from './Icon';
import { KanbanColumn, Program } from '@/lib/vster/types';
import { buildInitialBoard } from '@/lib/vster/data';

type ProgramItem = Program & { id?: number };

import HomeScreen from './screens/HomeScreen';
import ListScreen from './screens/ListScreen';
import DetailScreen from './screens/DetailScreen';
import EventsScreen from './screens/EventsScreen';
import MobileScreen from './screens/MobileScreen';
import OnboardingScreen from './screens/OnboardingScreen';
import WorkspaceShell from './workspace/WorkspaceShell';
import DashboardScreen from './workspace/DashboardScreen';
import KanbanScreen from './workspace/KanbanScreen';
import PlannerScreen from './workspace/PlannerScreen';
import SavedScreen from './workspace/SavedScreen';
import RecommendScreen from './workspace/RecommendScreen';

export interface HomeStats { newCount: number; openCount: number; closingSoon: number; agencyCount: number; siteCount: number; collectedAt: string }

export default function AppShell({ programs, homeStats, loggedIn, userEmail }: { programs?: ProgramItem[]; homeStats?: HomeStats; loggedIn?: boolean; userEmail?: string }) {
  const [screen, setScreen] = useState('home');
  const [obVariant, setObVariant] = useState<'A' | 'B'>('A');
  const [obStep, setObStep] = useState(1);
  const [board, setBoard] = useState<KanbanColumn[]>(buildInitialBoard());
  const [dragOverCol, setDragOverCol] = useState(-1);
  const [menuCol, setMenuCol] = useState(-1);
  const [addingCol, setAddingCol] = useState(-1);
  const [addingText, setAddingText] = useState('');
  const [editingCol, setEditingCol] = useState(-1);
  const [editingText, setEditingText] = useState('');
  const [plannerTarget, setPlannerTarget] = useState(0);
  const [evView, setEvView] = useState<'calendar' | 'card'>('calendar');
  const dragSrc = useRef<{ ci: number; di: number } | null>(null);

  const navigate = useCallback((s: string) => {
    setScreen(s);
    setMenuCol(-1);
    setAddingCol(-1);
    setEditingCol(-1);
    // 화면을 URL 에 반영 → 새로고침해도 같은 화면 유지(예: /?screen=recommend). home 은 '/'.
    try { window.history.replaceState(null, '', s === 'home' ? '/' : `/?screen=${s}`); } catch (_) {}
    try { window.scrollTo({ top: 0 }); } catch (_) {}
  }, []);

  // 최초 진입/새로고침 시 URL 의 ?screen= 로 화면 복원. (SSR 후 client 에서 1회)
  useEffect(() => {
    try {
      const s = new URLSearchParams(window.location.search).get('screen');
      const valid = ['home', 'list', 'events', 'dashboard', 'kanban', 'planner', 'saved', 'recommend'];
      if (s && valid.includes(s)) setScreen(s);
    } catch (_) {}
  }, []);

  const navigatePlanner = useCallback((idx: number) => {
    setScreen('planner');
    setPlannerTarget(idx);
    try { window.scrollTo({ top: 0 }); } catch (_) {}
  }, []);

  const isPublic = ['home', 'list', 'detail', 'events', 'mobile'].includes(screen);
  const isApp    = ['dashboard', 'kanban', 'planner', 'saved', 'recommend'].includes(screen);
  const showChrome = screen !== 'onboarding';

  // ── Kanban helpers ──────────────────────────────────────────────────────────
  const cloneBoard = (b: KanbanColumn[]) => b.map(c => ({ ...c, cards: c.cards.map(cd => ({ ...cd })) }));

  const moveCard = useCallback((dstCol: number, dstIndex: number) => {
    const src = dragSrc.current;
    if (!src) return;
    setBoard(prev => {
      const board = cloneBoard(prev);
      const [card] = board[src.ci].cards.splice(src.di, 1);
      let idx = dstIndex;
      if (src.ci === dstCol && src.di < dstIndex) idx--;
      if (idx < 0) idx = 0;
      board[dstCol].cards.splice(idx, 0, card);
      return board;
    });
    setDragOverCol(-1);
    dragSrc.current = null;
  }, []);

  const toggleMenu = useCallback((ci: number) => {
    setMenuCol(prev => prev === ci ? -1 : ci);
    setAddingCol(-1);
    setEditingCol(-1);
  }, []);

  const clearColumn = useCallback((ci: number) => {
    setBoard(prev => { const b = cloneBoard(prev); b[ci].cards = []; return b; });
    setMenuCol(-1);
  }, []);

  const deleteColumn = useCallback((ci: number) => {
    setBoard(prev => { const b = cloneBoard(prev); b.splice(ci, 1); return b; });
    setMenuCol(-1);
  }, []);

  const startEdit = useCallback((ci: number) => {
    setEditingCol(ci);
    setEditingText(board[ci].name);
    setMenuCol(-1);
  }, [board]);

  const commitEdit = useCallback(() => {
    setBoard(prev => {
      if (editingCol < 0) return prev;
      const b = cloneBoard(prev);
      const t = editingText.trim();
      if (t) b[editingCol].name = t;
      return b;
    });
    setEditingCol(-1);
  }, [editingCol, editingText]);

  const startAdd = useCallback((ci: number) => {
    setAddingCol(ci);
    setAddingText('');
    setMenuCol(-1);
    setEditingCol(-1);
  }, []);

  const commitAdd = useCallback(() => {
    const t = addingText.trim();
    if (!t) { setAddingCol(-1); setAddingText(''); return; }
    setBoard(prev => {
      const b = cloneBoard(prev);
      b[addingCol].cards.push({ id: 'c' + Date.now(), title: t, tag: '신규', dday: 'D-', ddBg: '#E7EDFF', ddColor: '#2C5CFF', amount: '금액 미정', memo: '' });
      return b;
    });
    setAddingCol(-1);
    setAddingText('');
  }, [addingCol, addingText]);

  const addColumn = useCallback(() => {
    setBoard(prev => [...cloneBoard(prev), { name: '새 단계', accent: '#737685', cards: [] }]);
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#F4F6FA', color: '#0E1726' }}>
      {showChrome && <Header screen={screen} navigate={navigate} loggedIn={loggedIn} userEmail={userEmail} />}

      {screen === 'home'       && <HomeScreen navigate={navigate} programs={programs} stats={homeStats} />}
      {screen === 'list'       && <ListScreen navigate={navigate} programs={programs} />}
      {screen === 'detail'     && <DetailScreen navigate={navigate} navigatePlanner={navigatePlanner} />}
      {screen === 'events'     && <EventsScreen navigate={navigate} evView={evView} setEvView={setEvView} />}
      {screen === 'mobile'     && <MobileScreen navigate={navigate} />}
      {screen === 'onboarding' && (
        <OnboardingScreen
          navigate={navigate}
          obStep={obStep} setObStep={setObStep}
          obVariant={obVariant} setObVariant={setObVariant}
          onFinish={() => navigate('dashboard')}
        />
      )}

      {isApp && (
        <WorkspaceShell screen={screen} navigate={navigate}>
          {screen === 'dashboard'  && <DashboardScreen navigate={navigate} />}
          {screen === 'kanban'     && (
            <KanbanScreen
              navigate={navigate}
              board={board} dragOverCol={dragOverCol} menuCol={menuCol}
              addingCol={addingCol} addingText={addingText}
              editingCol={editingCol} editingText={editingText}
              dragSrc={dragSrc}
              setDragOverCol={setDragOverCol}
              moveCard={moveCard} toggleMenu={toggleMenu}
              clearColumn={clearColumn} deleteColumn={deleteColumn}
              startEdit={startEdit} commitEdit={commitEdit}
              startAdd={startAdd} commitAdd={commitAdd}
              setAddingText={setAddingText} setEditingText={setEditingText}
              cancelAdd={() => { setAddingCol(-1); setAddingText(''); }}
              closeMenu={() => setMenuCol(-1)}
              addColumn={addColumn}
            />
          )}
          {screen === 'planner'    && <PlannerScreen navigate={navigate} plannerTarget={plannerTarget} setPlannerTarget={setPlannerTarget} />}
          {screen === 'saved'      && <SavedScreen navigate={navigate} />}
          {screen === 'recommend'  && <RecommendScreen navigate={navigate} />}
        </WorkspaceShell>
      )}

      {isPublic && <Footer />}

      {/* 채팅/문의 플로팅 버튼 — 기능 미연결(onClick 없음)이라 숨김 처리. 문의 기능 붙일 때 복원.
      {showChrome && (
        <button style={{ position: 'fixed', bottom: 26, right: 26, zIndex: 60, width: 60, height: 60, borderRadius: '50%', background: '#2C5CFF', border: 'none', cursor: 'pointer', boxShadow: '0 10px 26px rgba(0,61,155,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="forum" size={28} color="#fff" fill={1} />
        </button>
      )}
      */}
    </div>
  );
}
